import { useEffect, useRef, useMemo } from 'react';
import { useAppStore } from '../contexts/store';
import toast from 'react-hot-toast';

const DEFAULT_TIMEOUT_MINUTES = 15;
const WARNING_TIME = 2 * 60 * 1000;  // 2 minutes before logout

export function useIdleTimeout() {
  const { logout, token, tenantConfig } = useAppStore();
  const isAuthenticated = !!token;
  const timeoutRef = useRef<number | undefined>(undefined);
  const warningRef = useRef<number | undefined>(undefined);
  const warningShownRef = useRef(false);

  // Get timeout from tenant config, default to 15 minutes
  // If 0, idle timeout is disabled
  const timeoutMinutes = tenantConfig?.sessionTimeoutMinutes ?? DEFAULT_TIMEOUT_MINUTES;
  const idleTimeout = useMemo(() => timeoutMinutes * 60 * 1000, [timeoutMinutes]);
  const isDisabled = timeoutMinutes === 0;

  const resetTimer = () => {
    // Clear existing timers
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (warningRef.current) clearTimeout(warningRef.current);
    warningShownRef.current = false;

    if (!isAuthenticated || isDisabled) return;

    // Show warning before logout (only if timeout > warning time)
    if (idleTimeout > WARNING_TIME) {
      warningRef.current = window.setTimeout(() => {
        if (!warningShownRef.current) {
          warningShownRef.current = true;
          toast.error('You will be logged out in 2 minutes due to inactivity', {
            duration: 5000,
          });
        }
      }, idleTimeout - WARNING_TIME);
    }

    // Auto logout
    timeoutRef.current = window.setTimeout(() => {
      logout();
      window.location.href = '/login?reason=idle';
      toast.error('You have been logged out due to inactivity');
    }, idleTimeout);
  };

  useEffect(() => {
    if (!isAuthenticated || isDisabled) return;

    // Events that indicate user activity
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove'];

    // Throttle the reset to avoid excessive calls
    let throttleTimeout: number | undefined;
    const throttledReset = () => {
      if (!throttleTimeout) {
        throttleTimeout = window.setTimeout(() => {
          resetTimer();
          clearTimeout(throttleTimeout);
          throttleTimeout = undefined;
        }, 1000); // Only reset once per second max
      }
    };

    events.forEach(event => {
      document.addEventListener(event, throttledReset, true);
    });

    // Initial timer
    resetTimer();

    // Cleanup
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, throttledReset, true);
      });
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (warningRef.current) clearTimeout(warningRef.current);
      if (throttleTimeout) clearTimeout(throttleTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isDisabled, idleTimeout]);
}
